import { useState } from "react";
import { motion } from "framer-motion";
import { TrendingUp, ArrowUpRight } from "lucide-react";
import {
  AreaChart, Area, ResponsiveContainer, XAxis, Tooltip,
} from "recharts";

const cases = [
  {
    id: "homestack",
    industry: "Real Estate",
    client: "HomeStack Realty",
    headline: "From 12 leads/mo to 480+ qualified leads/mo",
    color: "#FF5E00",
    color2: "#FF007F",
    metrics: [
      { label: "Leads / month", before: 12, after: 480, suffix: "" },
      { label: "Conversion rate", before: 1.1, after: 6.4, suffix: "%", decimals: 1 },
      { label: "Revenue lift", before: 0, after: 14.2, prefix: "₹", suffix: "Cr", decimals: 1 },
    ],
    data: [
      { m: "Jan", v: 12 }, { m: "Feb", v: 28 }, { m: "Mar", v: 62 },
      { m: "Apr", v: 140 }, { m: "May", v: 240 }, { m: "Jun", v: 360 }, { m: "Jul", v: 480 },
    ],
  },
  {
    id: "lumen-edu",
    industry: "Education",
    client: "Lumen Academy",
    headline: "Built an AI demand engine generating 9,000 leads / quarter",
    color: "#00F0FF",
    color2: "#8A2BE2",
    metrics: [
      { label: "MQLs / quarter", before: 800, after: 9000, suffix: "" },
      { label: "Cost / lead", before: 720, after: 86, prefix: "₹", suffix: "" },
      { label: "Enrolments lift", before: 0, after: 3.4, suffix: "x", decimals: 1 },
    ],
    data: [
      { m: "Q1", v: 800 }, { m: "Q2", v: 2200 }, { m: "Q3", v: 5400 }, { m: "Q4", v: 9000 },
    ],
  },
  {
    id: "sigma-health",
    industry: "Healthcare",
    client: "Sigma Health",
    headline: "Automated CRM + AI agents replaced 4 internal roles",
    color: "#FF007F",
    color2: "#007AFF",
    metrics: [
      { label: "Ops time saved", before: 0, after: 71, suffix: "%" },
      { label: "Patient bookings", before: 1.0, after: 2.6, suffix: "x", decimals: 1 },
      { label: "Retention", before: 62, after: 91, suffix: "%" },
    ],
    data: [
      { m: "W1", v: 12 }, { m: "W4", v: 28 }, { m: "W8", v: 46 }, { m: "W12", v: 58 }, { m: "W16", v: 71 },
    ],
  },
];

export default function CaseStudies() {
  const [active, setActive] = useState(cases[0].id);
  const cur = cases.find((c) => c.id === active);

  return (
    <section id="case-studies" data-testid="case-studies-section" className="section relative">
      <div className="container-x">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-14">
          <div className="max-w-2xl">
            <p className="overline mb-4">Case Studies</p>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight">
              Real systems. Real <span className="text-gradient-neon">compounding</span> results.
            </h2>
          </div>
          <div className="flex flex-wrap gap-2">
            {cases.map((c) => (
              <button
                key={c.id}
                data-testid={`case-tab-${c.id}`}
                onClick={() => setActive(c.id)}
                className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${
                  active === c.id
                    ? "bg-white text-bg-0"
                    : "bg-white/5 text-white/70 hover:bg-white/10"
                }`}
              >
                {c.industry}
              </button>
            ))}
          </div>
        </div>

        <motion.div
          key={cur.id}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="grid grid-cols-1 lg:grid-cols-12 gap-5"
          data-testid="case-study-active"
        >
          {/* Story */}
          <div className="lg:col-span-5 glass rounded-3xl p-8 relative overflow-hidden">
            <div
              className="absolute -top-32 -left-32 w-80 h-80 rounded-full blur-3xl opacity-40"
              style={{ background: `radial-gradient(circle, ${cur.color}, transparent)` }}
            />
            <p className="overline mb-4">{cur.industry} · {cur.client}</p>
            <h3 className="text-2xl sm:text-3xl font-bold tracking-tight leading-tight">
              {cur.headline}
            </h3>
            <p className="mt-4 text-white/55 text-sm leading-relaxed">
              We rebuilt the team&apos;s growth stack from the ground up — new brand,
              new acquisition engine, automated CRM, and conversion-tuned funnels —
              wired together with measurable revenue attribution.
            </p>
            <div className="mt-6 flex items-center gap-2 text-sm font-semibold">
              <span style={{ color: cur.color }}>Read the full story</span>
              <ArrowUpRight size={16} style={{ color: cur.color }} />
            </div>
          </div>

          {/* Chart */}
          <div className="lg:col-span-7 glass rounded-3xl p-8 relative overflow-hidden">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="overline">Growth Curve</p>
                <div className="mt-1 flex items-center gap-2">
                  <TrendingUp size={18} className="text-emerald-400" />
                  <span className="font-mono text-xl font-bold">Trajectory: up & right</span>
                </div>
              </div>
              <div className="flex items-center gap-3 text-[11px] font-mono uppercase tracking-[0.18em] text-white/55">
                <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ background: cur.color }} /> Before</span>
                <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full" style={{ background: cur.color2 }} /> After</span>
              </div>
            </div>

            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={cur.data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id={`g-${cur.id}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={cur.color} stopOpacity={0.85} />
                      <stop offset="100%" stopColor={cur.color2} stopOpacity={0.05} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="m" stroke="rgba(255,255,255,0.3)" tick={{ fontSize: 11 }} />
                  <Tooltip
                    cursor={{ stroke: "rgba(255,255,255,0.2)" }}
                    contentStyle={{
                      background: "rgba(10,10,18,0.92)",
                      border: "1px solid rgba(255,255,255,0.1)",
                      borderRadius: 12,
                      fontFamily: "JetBrains Mono",
                      fontSize: 12,
                    }}
                  />
                  <Area
                    type="monotone"
                    dataKey="v"
                    stroke={cur.color}
                    strokeWidth={2.5}
                    fill={`url(#g-${cur.id})`}
                    isAnimationActive={true}
                    animationDuration={1200}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            <div className="grid grid-cols-3 gap-3 mt-6">
              {cur.metrics.map((m) => (
                <div key={m.label} className="rounded-xl bg-white/[0.03] border border-white/10 p-4">
                  <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-white/45">
                    {m.label}
                  </div>
                  <div className="mt-2 flex items-baseline gap-2">
                    <span className="text-white/40 line-through text-sm">
                      {m.prefix || ""}{m.before}{m.suffix || ""}
                    </span>
                    <span
                      className="font-mono text-lg font-extrabold"
                      style={{ color: cur.color }}
                    >
                      {m.prefix || ""}{m.after}{m.suffix || ""}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
